import { useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  List,
  ListItem,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { useCommunityChannels } from "../hooks/useCommunityChannels";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

export function CommunityChannels({
  communityId,
  compact = false,
}: {
  communityId: string;
  compact?: boolean;
}) {
  const { user, loading } = useAuth();
  if (loading || !user) return <Typography role="status">
    {loading ? "Loading channels…" : "Sign in to view channels."}
  </Typography>;
  return (
    <ScopedCommunityChannels key={`${user.uid}:${communityId}`} communityId={communityId} compact={compact} />
  );
}

function ScopedCommunityChannels({ communityId, compact }: { communityId: string; compact: boolean }) {
  const { channels, create } = useCommunityChannels(communityId);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      !name.trim() ||
      create.isPending ||
      !channels.data?.can_manage_channels ||
      channels.isError
    )
      return;
    try {
      await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
      });
      setName("");
      setDescription("");
      setEditing(false);
    } catch {
      // Keep the draft. Mutation state displays the failure; authority refetches.
    }
  }

  // A failed authorization refresh must not render a cached protected list.
  if (channels.isError) {
    return (
      <Alert
        severity="warning"
        action={
          <Button size="small" onClick={() => void channels.refetch()}>
            Retry
          </Button>
        }
      >
        Channels are unavailable. Your community access may have changed.
      </Alert>
    );
  }
  if (!channels.data)
    return <Typography role="status">Loading channels…</Typography>;
  const data = channels.data;
  const deadline = data.read_only_at ? new Date(data.read_only_at) : null;
  const deadlineLabel = deadline && Number.isFinite(deadline.getTime())
    ? deadline.toLocaleString("en-US", {
        timeZone: "UTC", timeZoneName: "short", year: "numeric", month: "short",
        day: "numeric", hour: "numeric", minute: "2-digit",
      })
    : null;

  return (
    <Stack component="section" spacing={1.5} aria-label="Channels">
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
        spacing={1}
      >
        <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700 }}>
          {compact ? "Chats" : "Channels"}
        </Typography>
        {compact ? (
          <Link
            component={RouterLink}
            to={`/community/communities/${communityId}?view=chat`}
            underline="none"
            aria-label="All chats"
            sx={{ fontSize: "0.875rem", fontWeight: 600 }}
          >
            All
          </Link>
        ) : data.can_manage_channels && !editing ? (
          <Button size="small" onClick={() => setEditing(true)}>
            Add channel
          </Button>
        ) : null}
      </Stack>
      {data.read_only_at ? (
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            {deadlineLabel ? `Learner posting deadline: ${deadlineLabel}.` : "The posting deadline is unavailable right now."}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.can_manage_channels && data.write_access === "allowed"
              ? "You can continue to reply as staff after the learner deadline."
              : data.write_access === "read_only"
                ? "Your access is read-only. You can still open channels to view conversation history."
                : "Arrange continued support before the deadline. Conversation history remains available afterward."}
          </Typography>
        </Stack>
      ) : null}
      {editing && data.can_manage_channels ? (
        <Stack
          component="form"
          spacing={1.5}
          onSubmit={(event) => void submit(event)}
        >
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            size="small"
            slotProps={{ htmlInput: { maxLength: 80 } }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={2}
            size="small"
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          {create.isError ? (
            <GuardianAwareError error={create.error}>
              <Alert severity="error">
                Could not create the channel. Please try again.
              </Alert>
            </GuardianAwareError>
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button
              type="submit"
              size="small"
              variant="contained"
              disabled={create.isPending || !name.trim()}
            >
              Create channel
            </Button>
            <Button
              size="small"
              disabled={create.isPending}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : null}
      {data.items.length === 0 ? (
        <Typography color="text.secondary">No channels yet.</Typography>
      ) : (
        <List disablePadding>
          {(compact ? data.items.slice(0, 3) : data.items).map((channel) => (
            <ListItem key={channel.conversation_id} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={`/community/communities/${communityId}/channels/${channel.conversation_id}`}
                sx={{ borderRadius: 2, gap: 1.5, px: 0.5, py: 0.75 }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    color: "text.secondary",
                    display: "grid",
                    placeItems: "center",
                    fontWeight: 700,
                  }}
                >
                  #
                </Box>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: channel.unread_count > 0 ? 700 : 500 }}>
                    {channel.name}
                  </Typography>
                  {channel.description ? (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {channel.description}
                    </Typography>
                  ) : null}
                </Stack>
                {channel.unread_count > 0 ? (
                  <Chip
                    size="small"
                    color="primary"
                    label={`${channel.unread_count} unread`}
                  />
                ) : null}
                {channel.archived_at ? (
                  <Chip size="small" label="Archived" variant="outlined" />
                ) : channel.write_access === "read_only" ? (
                  <Chip size="small" label="Read-only" variant="outlined" />
                ) : null}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Stack>
  );
}
